import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Pencil, Trash2, Image as ImageIcon } from 'lucide-react';
import {
  BANNER_PRESETS,
  resolveBannerVisual,
  visualBackground,
  BANNER_AUDIENCES,
  BANNER_PLATFORMS,
  BANNER_ICONS,
  Banner,
} from '../../../api/banners';
import { EmptyState, StatusPill, type StatusTone } from '@/components/app';
import { formatTashkentShort } from '@/lib/datetime';
import { BannersTableProps } from '../types';

const formatDate = (date: string | null) => (date ? formatTashkentShort(date) : '—');

/**
 * Почему баннер не показывается, даже когда включён. Пользователь админки
 * иначе будет гадать, отчего акция «не работает».
 */
function getStatus(banner: Banner): { label: string; tone: StatusTone } {
  if (!banner.is_active) return { label: 'Выключен', tone: 'neutral' };

  const now = Date.now();
  if (banner.starts_at && new Date(banner.starts_at).getTime() > now) {
    return { label: 'Запланирован', tone: 'warn' };
  }
  if (banner.ends_at && new Date(banner.ends_at).getTime() < now) {
    return { label: 'Истёк', tone: 'neutral' };
  }
  return { label: 'Показывается', tone: 'pos' };
}

export function BannersTable({
  banners,
  isLoading,
  selectedId,
  onEdit,
  onDelete,
  onToggleActive,
  onCreate,
}: BannersTableProps) {
  if (!isLoading && banners.length === 0) {
    return (
      <EmptyState
        icon={<ImageIcon />}
        title="Баннеров пока нет"
        description="Создайте баннер — он появится на главном экране приложения"
        action={onCreate && <Button variant="secondary" onClick={onCreate}>Создать первый баннер</Button>}
        className="border-t border-border-soft"
      />
    );
  }

  return (
    <Table className="min-w-[980px]">
      <TableHeader>
        <TableRow>
          <TableHead>Баннер</TableHead>
          <TableHead>Оформление</TableHead>
          <TableHead>Кому</TableHead>
          <TableHead className="text-right">Приоритет</TableHead>
          <TableHead>Период</TableHead>
          <TableHead className="text-right">Показы / CTR</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead className="text-right">Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading
          ? Array.from({ length: 4 }, (_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 8 }, (__, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full max-w-28" /></TableCell>
                ))}
              </TableRow>
            ))
          : banners.map((banner) => {
              const status = getStatus(banner);
              const preset = BANNER_PRESETS.find((p) => p.value === banner.preset);
              // Свотч в светлой теме приложения, с учётом ручных цветов, если они заданы.
              const swatch = resolveBannerVisual(banner.preset, false, banner.custom_style);
              const icon = BANNER_ICONS.find((i) => i.value === banner.icon);

              return (
                <TableRow key={banner.id} selected={selectedId === banner.id}>
                  <TableCell className="max-w-[280px]">
                    <div className="font-semibold">{banner.name}</div>
                    <div className="mt-0.5 truncate text-[13px] text-text-2">
                      {banner.title?.ru || banner.title?.uz || banner.title?.kiril || '—'}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2">
                      {/* Цвета баннера — данные (пресет/ручные), а не тема админки. */}
                      <span
                        className="inline-flex size-7 items-center justify-center rounded-lg text-xs"
                        style={{ background: visualBackground(swatch), color: swatch.fg }}
                        title={preset?.label}
                        aria-hidden
                      >
                        {icon?.label.split(' ')[0] ?? '•'}
                      </span>
                      <span className="text-[13px] text-text-2">{preset?.label ?? banner.preset}</span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <span>{BANNER_AUDIENCES.find((a) => a.value === banner.audience)?.label}</span>
                      {banner.platform !== 'all' && (
                        <StatusPill size="sm">{BANNER_PLATFORMS.find((p) => p.value === banner.platform)?.label}</StatusPill>
                      )}
                    </div>
                  </TableCell>

                  <TableCell className="text-right font-semibold tabular-nums">{banner.priority}</TableCell>

                  <TableCell className="whitespace-nowrap text-[13px] tabular-nums text-text-2">
                    {formatDate(banner.starts_at)} — {formatDate(banner.ends_at)}
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="tabular-nums">{banner.impressions_count.toLocaleString('ru-RU')}</div>
                    <div className="text-[13px] tabular-nums text-text-2">
                      {banner.clicks_count.toLocaleString('ru-RU')} кликов · {(banner.ctr * 100).toFixed(1)}%
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <Switch
                        checked={banner.is_active}
                        onCheckedChange={() => onToggleActive(banner)}
                        aria-label={`Баннер включён: ${banner.name}`}
                      />
                      <StatusPill tone={status.tone}>{status.label}</StatusPill>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon-sm" aria-label="Редактировать баннер" title="Редактировать" onClick={() => onEdit(banner)}>
                        <Pencil />
                      </Button>
                      <Button variant="ghost" size="icon-sm" aria-label="Удалить баннер" title="Удалить" onClick={() => onDelete(banner.id)} className="text-neg hover:bg-neg-soft">
                        <Trash2 />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
      </TableBody>
    </Table>
  );
}
